import React, { ReactNode, useCallback, useEffect, useState } from "react";

import { Dispatch, State } from "./state/types";
import { Gutters, withinGutter } from "./useGutters";

type Props = Readonly<{
  children: ReactNode;
  dispatch: Dispatch;
  gutters: Gutters;
  height: number;
  review: boolean;
  scale: number;
  state: State;
  width: number;
  xOffset: number;
  yOffset: number;
  zoom: number;
}>;

const getMousePosition = (e: React.MouseEvent<HTMLDivElement>, zoom: number): [number, number] => {
  const bounds = e.currentTarget.getBoundingClientRect();
  return [
    (e.clientX + e.currentTarget.scrollLeft - bounds.left) / zoom,
    (e.clientY + e.currentTarget.scrollTop - bounds.top) / zoom,
  ];
};

export const InputHandler = ({
  children,
  dispatch,
  gutters,
  height,
  review,
  scale,
  state,
  width,
  xOffset,
  yOffset,
  zoom,
}: Props) => {
  const [inputRef, setInputRef] = useState<HTMLDivElement | null>(null);
  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (review) {
        return;
      }
      const position = getMousePosition(e, zoom);
      const [x, y] = position;
      if (withinGutter(gutters.top, position) || withinGutter(gutters.bottom, position)) {
        dispatch({
          type: "ADD COLUMN",
          x: (x - xOffset - gutters.left.width) / scale,
        });
      } else if (withinGutter(gutters.left, position) || withinGutter(gutters.right, position)) {
        dispatch({
          type: "ADD ROW",
          y: (y - yOffset - gutters.top.height) / scale,
        });
      } else if (
        x > gutters.left.x + gutters.left.width &&
        x < gutters.right.x &&
        y > gutters.top.y + gutters.top.height &&
        y < gutters.bottom.y
      ) {
        dispatch({
          position: [
            (x - xOffset - gutters.left.width) / scale,
            (y - yOffset - gutters.top.height) / scale,
          ],
          shiftClick: e.shiftKey,
          scale,
          type: "SET MOUSE DRAG POSITION",
        });
      }
    },
    [dispatch, gutters, height, review, width, xOffset, yOffset, zoom],
  );
  const handleMouseUp = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (review) {
      return;
    }
    dispatch(
      { 
        type: "CLEAR MOUSE DRAG POSITION",
        shiftKey: e.shiftKey
      });
  }, [dispatch, review]);
  const handleMouseLeave = useCallback(() => {
    if (review) {
      return;
    }
    dispatch({ type: "CLEAR MOUSE POSITION" });
  }, [dispatch, review]);
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (review) {
        return;
      }
      const position = getMousePosition(e, zoom);
      const [x, y] = position;
      if (withinGutter(gutters.top, position) || withinGutter(gutters.bottom, position)) {
        if (state.mouseDragStart) {
          return;
        }
        dispatch({
          gutter: "horizontal",
          position,
          type: "SET WITHIN GUTTER",
        });
        return;
      }
      if (withinGutter(gutters.left, position) || withinGutter(gutters.right, position)) {
        if (state.mouseDragStart) {
          return;
        }
        dispatch({
          gutter: "vertical",
          position,
          type: "SET WITHIN GUTTER",
        });
        return;
      }
      if (
        x >= gutters.left.x + gutters.left.width &&
        x <= gutters.right.x &&
        y >= gutters.top.y + gutters.top.height &&
        y <= gutters.bottom.y
      ) {
        dispatch({
          position: [
            (x - gutters.left.x - gutters.left.width) / scale,
            (y - gutters.top.y - gutters.top.height) / scale,
          ],
          type: "SET MOUSE POSITION",
        });
        return;
      }
      dispatch({ type: "CLEAR MOUSE POSITION" });
    },
    [dispatch, gutters, height, review, state.mouseDragStart, width, zoom],
  );
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {

      const asNumber = parseInt(e.key, 10);
      if (!review && !isNaN(asNumber) && asNumber > 0) {
        dispatch({
          number: asNumber,
          type: "NUMBER PRESSED",
        });
      }

      const key = e.key.toLowerCase();
      switch (key) {
        case "d":
        case "backspace":
        case "delete": {
          if (review) {
            return;
          }
          dispatch({ type: "DELETE SELECTED SEGMENTS" });
          break;
        }
        case "z": {
          if (review) {
            return;
          }
          if (e.ctrlKey) {
            dispatch({ type: e.shiftKey ? "REDO" : "UNDO" });
          }
          break;
        }
        case "alt":
        case "space":
        case "spacebar":
        case " ": {
          e.preventDefault();
          e.stopPropagation();
          dispatch({ type: "HIDE TABLE UI" });
          break;
        }
        case "c":
        case "r": {
          if (review) {
            return;
          }
          dispatch({
            direction: key === "c" ? "col" : "row",
            type: "SPLIT CELL",
          });
          break;
        }
        case "i":
          if (!review) {
            dispatch({
              type: "TOGGLE BORDER EXPLICITNESS",
            });
          }
          break;
        case "m":
          if (!review) {
            dispatch({
              type: "MERGE CELLS",
            });
          }
          break;
        case "arrowup":
        case "arrowleft":
        case "arrowdown":
        case "arrowright":
        case "tab":
          e.preventDefault();
          e.stopPropagation();
          dispatch({
            key: key,
            shiftKey: e.shiftKey,
            type: "MOVE TO NEXT CELL",
          });
          break; 
      }
    },
    [dispatch, review],
  );
  const handleKeyUp = useCallback(
    (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      switch (key) {
        case "alt":
        case " ": {
          dispatch({ type: "SHOW TABLE UI" });
          break;
        }
      }
    },
    [dispatch, review],
  );
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);
  useEffect(() => {
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleKeyUp]);
  useEffect(() => {
    if (!inputRef) {
      return;
    }
    if (
      inputRef.scrollHeight > inputRef.clientHeight ||
      inputRef.scrollWidth > inputRef.clientWidth
    ) {
      // centre the scroll position
      inputRef.scrollTop = (inputRef.scrollHeight - inputRef.clientHeight) / 2;
      inputRef.scrollLeft = (inputRef.scrollWidth - inputRef.clientWidth) / 2;
    } else {
      inputRef.scrollTop = 0;
      inputRef.scrollLeft = 0;
    }
  }, [zoom]);
  return (
    <div
      className="table-zoning-input-handler"
      onMouseDown={handleMouseDown}
      onMouseEnter={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      ref={setInputRef}
    >
      {children}
    </div>
  );
};
